import React, { useState, useRef, useEffect } from 'react';
import Button from '../Components/Buttons';
import { CustomInput } from '../Components/CustomFormElements';
import { useAlert, AlertProvider } from '../Components/AlertContext';
import { useConfirmation, ConfirmationProvider } from '../Components/ConfirmationContext';
import './EntityBox.css'; // Ensure to add relevant styling for the box and dropdown menu.

const EntityBox = () => {
  const [entities, setEntities] = useState([]); // List of added entities
  const [inputValue, setInputValue] = useState(''); // Input value
  const [isError, setIsError] = useState(false); // Track input error state
  const [dropdownOpen, setDropdownOpen] = useState(null); // Track which dropdown is open
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const dropdownRefs = useRef([]); // Track references to all dropdowns
  const textareaRef = useRef(null); // Ref for textarea

  const handleAddEntity = () => {
    const sanitizedValue = inputValue.trim();

    // Validate input: no empty value or special characters
    if (sanitizedValue === '') {
      setIsError(true);
      showAlert('fail', 'Input cannot be empty!');
      return;
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(sanitizedValue)) {
      setIsError(true);
      showAlert('fail', 'Special characters are not allowed!');
      return;
    }

    setEntities([...entities, sanitizedValue]); // Add entity to the list
    setInputValue(''); // Clear input field
    setIsError(false); // Remove error class
  };

  const handleInputChange = (id, value) => {
    setInputValue(value);
    setIsError(false); // Remove error class when typing
  };

  const handleDeleteEntity = (entityToDelete) => {
    showConfirmation('Are you sure you want to delete this item?', () => {
      try {
        setEntities((prevEntities) => prevEntities.filter((entity) => entity !== entityToDelete));
        showAlert('success', 'Item was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      }
    });
  };

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index); // Toggle dropdown visibility
  };

  // Close dropdown menu when clicked outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      // Check if the click is outside all dropdowns
      if (!dropdownRefs.current.some((ref) => ref?.contains(event.target))) {
        setDropdownOpen(null); // Close the dropdown if clicked outside
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleDragStart = (event, entity) => {
    const dragImage = document.createElement('div');
    dragImage.textContent = `{${entity}}`;
    dragImage.className = 'drag-image'; // Apply the CSS class
    document.body.appendChild(dragImage);

    // Center the drag image under the pointer
    event.dataTransfer.setDragImage(dragImage, dragImage.offsetWidth / 2, dragImage.offsetHeight / 2);

    // Add the data payload
    event.dataTransfer.setData('text/plain', `{${entity}}`);

    // Remove the drag image after a short delay to avoid leaks
    setTimeout(() => {
        document.body.removeChild(dragImage);
    }, 0);
};


  return (
    <div className="entity-box">
      {/* Input and Add Button */}
      <div className="input-container">
        <CustomInput
          id="entity-name"
          placeholder="Enter entity name"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); // Prevent default form submission behavior
              handleAddEntity();
            }
          }}
          type="text"
          className={isError ? 'input-error' : ''}
        />
        <button className="entity-button" onClick={handleAddEntity}>
          Add
        </button>
      </div>

      {/* Display entities */}
      <div className="entities-list">
        {entities.map((entity, index) => (
          <div
            key={index}
            className="entity-item"
            draggable
            onDragStart={(event) => handleDragStart(event, entity)} // Enable dragging
          >
            <span>{`${entity}`}</span>
            <button className='entity-delete-button' onClick={() => handleDeleteEntity(entity)}></button>
            
          </div>
        ))}
      </div>
    </div>
  );
};

const EntityBoxWrapper = () => (
  <AlertProvider>
    <ConfirmationProvider>
      <EntityBox />
    </ConfirmationProvider>
  </AlertProvider>
);

export default EntityBoxWrapper;